import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Row, Col, Tabs, Tab, Container, } from "react-bootstrap";
import { toast } from "react-toastify";
import "../../candidatedetails.css";
import { IoIosArrowForward } from "react-icons/io";
import { getUser, getUserSubscription } from "../../store/slices/user";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Form } from "react-bootstrap";
import Select from "react-select";
import {
  FaUser,
  FaMale,
  FaFemale,
  FaHome,
  FaMapMarkerAlt,
  FaFileAlt,
  FaEye,
} from "react-icons/fa";

import "react-photo-view/dist/react-photo-view.css";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { assignPlan, getPlan } from "../../store/slices/plan";

export default function CandidateDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [candidate, setCandidate] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [assigning, setAssigning] = useState(false);
  useEffect(() => {
    dispatch(getPlan({ status: "active" }))
      .unwrap()
      .then((res) => {
        setPlans(res?.data || []);
      });
  }, [dispatch]);


  useEffect(() => {
    if (id) {
      dispatch(getUser({ id }))
        .unwrap()
        .then((res) => {
          const userData = Array.isArray(res?.data) ? res.data[0] : res?.data;
          if (userData) setCandidate(userData);
        })
        .catch((err) => {
          toast.error(err?.message || "Failed to load candidate details.");
        });
    }
  }, [id]);

  useEffect(() => {
    dispatch(getUserSubscription({ id }));
  }, [dispatch, id]);

  const { userSubscription } = useSelector((state) => state.user);
  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getAge = (dob) => {
    if (!dob) return "—";
    return Math.floor(
      (Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25),
    );
  };

  const buildDocuments = (cand) => {
    if (!cand) return [];
    const docFields = [
      { key: "profilePhoto", label: "Profile Photo" },
      { key: "aadhaar", label: "Aadhaar" },
    ];
    return docFields
      .filter((d) => cand[d.key])
      .map((d) => ({
        documentType: d.label,
        url: cand[d.key],
        uploadedAt: cand.createdAt,
      }));
  };

  if (!candidate)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              border: "3px solid #7a1232",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p
            style={{
              color: "#7a1232",
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 18,
            }}
          >
            Loading profile…
          </p>
        </div>
      </div>
    );

  const profile = candidate.profile || {};
  const profession = candidate.profession || {};
  const family = candidate.family || {};
  const photos = candidate?.profilePhoto || "";
  const gallery = profile?.photos || [];
  const presentAddr = profile.presentAddress || {};
  const permanentAddr = profile.permanentAddress || {};
  const siblings = Array.isArray(family.siblings) ? family.siblings : [];


  const handleAssignPlan = async () => {
    if (!selectedPlan) {
      toast.error("Please select a plan.");
      return;
    }

    try {
      setAssigning(true);

      await dispatch(
        assignPlan({
          userId: id,
          planId: selectedPlan.value,
        })
      ).unwrap();

      toast.success("Plan assigned successfully.");

      setShowPlanModal(false);

      dispatch(getUserSubscription({ id }));
    } catch (err) {
      toast.error(err?.message || "Failed to assign plan.");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      <Container fluid className="cd-page">
        {/* Breadcrumb */}
        <div className="cd-breadcrumb">
          <Link to="/admin/home">Home</Link>
          <span className="sep">
            <IoIosArrowForward size={13} />
          </span>
          <span
            className="crumb-link"
            onClick={() => navigate("/admin/candidates")}
          >
            Candidate
          </span>
          <span className="sep">
            <IoIosArrowForward size={13} />
          </span>
          <span className="current">Candidate Details</span>
        </div>

        {/* Hero Card */}
        <div className="cd-hero">
          <div className="cd-hero-border" />
          <div className="cd-hero-inner">
            <div className="cd-avatar">
              {photos ? (
                <img src={photos} alt="profile" />
              ) : candidate.name ? (
                candidate.name.charAt(0).toUpperCase()
              ) : (
                "?"
              )}
            </div>
            <div className="cd-hero-info">
              <h3>{candidate.name || "—"}</h3>
              <p className="sub">
                {profession.occupation || "—"} &nbsp;·&nbsp;
                {candidate.dob ? `${getAge(candidate.dob)} yrs` : "—"}{" "}
                &nbsp;·&nbsp;
                {profile.height || "—"}
              </p>
              <span
                className={`cd-badge ${candidate.profileVerified ? "verified" : "unverified"}`}
              >
                {candidate.profileVerified
                  ? "✦ Profile Verified"
                  : "⚠ Not Verified"}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="cd-stats">
          {[
            { val: profession.education, label: "Education" },
            { val: candidate.dob ? getAge(candidate.dob) : "—", label: "Age" },
            { val: profession.occupation, label: "Occupation" },
            { val: presentAddr.city, label: "Location" },
            { val: candidate.mobileNumber, label: "Mobile" },
            { val: candidate.email, label: "Email" },
          ].map(({ val, label }, i) => (
            <div className="cd-stat-card" key={i}>
              <div className="cd-stat-val" title={val || "—"}>
                {val || "—"}
              </div>
              <div className="cd-stat-label">{label}</div>
            </div>
          ))}
        </div>

        {/* Main Card with Tabs */}
        <div className="cd-card cd-tabs">
          <Tabs defaultActiveKey="personal" className="custom-tabs">
            {/* ── Personal Info ── */}
            <Tab eventKey="personal" title="Personal Info">
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <div className="cd-info-box">
                    <div className="cd-info-box-title">Basic Details</div>
                    {[
                      ["Full Name", candidate.name],
                      ["Gender", candidate.gender],
                      ["Email", candidate.email],
                      ["Mobile", candidate.mobileNumber],
                      ["Date of Birth", formatDate(candidate.dob)],
                      ["Profile For", candidate.profileFor],
                      ["Joined On", formatDate(candidate.createdAt)],
                    ].map(([label, val], i) => (
                      <div className="cd-info-row" key={i}>
                        <div className="cd-info-icon">
                          <FaUser />
                        </div>
                        <span className="cd-info-label">{label}</span>
                        <span className="cd-info-val">{val || "—"}</span>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="cd-info-box">
                    <div className="cd-info-box-title">Profile Details</div>
                    {[
                      ["Height", profile.height],
                      ["Marital Status", profile.maritalStatus],
                      ["Blood Group", profile.bloodGroup],
                      ["Complexion", profile.complexion],
                      ["Mother Tongue", profile.motherTongue],
                      ["Sub Caste", profile.subCaste],
                      ["Native District", profile.nativeDistrict],
                    ].map(([label, val], i) => (
                      <div className="cd-info-row" key={i}>
                        <div className="cd-info-icon">
                          {i === 6 ? <FaMapMarkerAlt /> : <FaUser />}
                        </div>
                        <span className="cd-info-label">{label}</span>
                        <span className="cd-info-val">{val || "—"}</span>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="cd-info-box">
                    <div className="cd-info-box-title">Present Address</div>
                    {[
                      ["Address", presentAddr.address],
                      ["City", presentAddr.city],
                      ["State", presentAddr.state],
                      ["Country", presentAddr.country],
                      ["Staying As", presentAddr.staying],
                      ["Planning to Settle", presentAddr.planningtoSettle],
                    ].map(([label, val], i) => (
                      <div className="cd-info-row" key={i}>
                        <div className="cd-info-icon">
                          {i >= 4 ? <FaHome /> : <FaMapMarkerAlt />}
                        </div>
                        <span className="cd-info-label">{label}</span>
                        <span className="cd-info-val">{val || "—"}</span>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="cd-info-box">
                    <div className="cd-info-box-title">Permanent Address</div>
                    {[
                      ["Address", permanentAddr.address],
                      ["City", permanentAddr.city],
                      ["State", permanentAddr.state],
                      ["Country", permanentAddr.country],
                    ].map(([label, val], i) => (
                      <div className="cd-info-row" key={i}>
                        <div className="cd-info-icon">
                          <FaMapMarkerAlt />
                        </div>
                        <span className="cd-info-label">{label}</span>
                        <span className="cd-info-val">{val || "—"}</span>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col md={12}>
                  <div className="cd-info-box">
                    <div className="cd-info-box-title">About</div>
                    <Row>


                      <Col md={12}>
                        <div className="cd-info-row">
                          <div className="cd-info-icon">
                            <FaUser />
                          </div>
                          <span className="cd-info-label">Languages Known</span>
                          <span className="cd-info-val">
                            {profile.languagesKnown?.join(", ") || "—"}
                          </span>
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="cd-info-row">
                          <div className="cd-info-icon">
                            <FaUser />
                          </div>
                          <span className="cd-info-label">About</span>
                          <span className="cd-info-val">
                            {profile.aboutMe || "—"}
                          </span>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </Tab>

            {/* ── Profession ── */}
            <Tab eventKey="profession" title="Profession">
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <div className="cd-info-box">
                    <div className="cd-info-box-title">Education & Career</div>
                    {[
                      ["Education", profession.education],
                      ["College", profession.college],
                      ["Occupation", profession.occupation],
                      ["Company", profession.companyName],
                      ["Employment Type", profession.employmentType],
                      ["Annual Income", profession.annualIncome],
                      ["Work Location", profession.workLocation],
                    ].map(([label, val], i) => (
                      <div className="cd-info-row" key={i}>
                        <div className="cd-info-icon">
                          {i === 6 ? <FaMapMarkerAlt /> : <FaUser />}
                        </div>
                        <span className="cd-info-label">{label}</span>
                        <span className="cd-info-val">{val || "—"}</span>
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            </Tab>

            {/* ── Family ── */}
            <Tab eventKey="family" title="Family">
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <div className="cd-info-box">
                    <div className="cd-info-box-title">Parents</div>
                    {[
                      [<FaMale />, "Father Name", family.fatherName],
                      [
                        <FaMale />,
                        "Father Occupation",
                        family.fatherOccupation,
                      ],
                      [<FaFemale />, "Mother Name", family.motherName],
                      [
                        <FaFemale />,
                        "Mother Occupation",
                        family.motherOccupation,
                      ],
                      [<FaHome />, "Family Type", family.familyType],
                    ].map(([icon, label, val], i) => (
                      <div className="cd-info-row" key={i}>
                        <div className="cd-info-icon">{icon}</div>
                        <span className="cd-info-label">{label}</span>
                        <span className="cd-info-val">{val || "—"}</span>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="cd-info-box">
                    <div className="cd-info-box-title">Siblings</div>
                    {siblings.length === 0 ? (
                      <p
                        style={{
                          color: "var(--text-muted)",
                          fontSize: 13,
                          marginTop: 8,
                        }}
                      >
                        No siblings info available.
                      </p>
                    ) : (
                      siblings.map((sib, i) => (
                        <div className="cd-info-row" key={i}>
                          <div className="cd-info-icon">
                            <FaUser />
                          </div>
                          <span className="cd-info-label">Sibling {i + 1}</span>
                          <span className="cd-info-val">
                            {sib.name} — {sib.occupation} ({sib.maritalStatus})
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </Col>
              </Row>
            </Tab>

            {/* ── Photos ── */}
            <Tab eventKey="photos" title="Photos">
              <Row className="g-3 mt-1">
                {gallery.length === 0 ? (
                  <Col>
                    <div className="cd-doc-empty">
                      <FaFileAlt />
                      <p style={{ margin: 0, fontSize: 14 }}>
                        No photos uploaded yet.
                      </p>
                    </div>
                  </Col>
                ) : (
                  <PhotoProvider
                    bannerVisible={false}
                    maskOpacity={0.85}
                    photoClosable
                  >
                    {gallery.map((src, i) => (
                      <Col lg={3} md={4} sm={6} xs={6} key={i}>
                        <PhotoView src={src}>
                          <div className="cd-gallery-item">
                            <img src={src} alt={`photo-${i + 1}`} />
                            <div className="cd-gallery-overlay">
                              <FaEye />
                            </div>
                          </div>
                        </PhotoView>
                      </Col>
                    ))}
                  </PhotoProvider>)
                }
              </Row>
            </Tab>
            <Tab eventKey="subscription" title="Subscription">
              <div className="subscription-right">

                <button
                  className="subscription-btn mt-3 mb-3"
                  onClick={() => {
                    setSelectedPlan(null);
                    setShowPlanModal(true);
                  }}
                >
                  + Add Plan
                </button>
              </div>
              {userSubscription ? (
                <div className="subscription-card">
                  <div className="subscription-header">

                    <div className="subscription-left">
                      <div className="subscription-title">
                        CURRENT PLAN
                      </div>

                      <h5>{userSubscription.planSnapshot?.name}</h5>

                      <p>
                        {formatDate(userSubscription.planStartsAt)}
                        &nbsp;•&nbsp;
                        {formatDate(userSubscription.planExpiresAt)}
                      </p>

                      <div className="subscription-usage">
                        <div>
                          <small>Chats Used</small>
                          <strong>{userSubscription.usage?.chatsUsed}</strong>
                        </div>

                        <div>
                          <small>Contact Views Used</small>
                          <strong>{userSubscription.usage?.contactViewsUsed}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="subscription-right">
                      <h5>
                        ₹{userSubscription.planSnapshot?.price}
                      </h5>

                      <span
                        className={`subscription-status ${userSubscription.status === "active"
                          ? "active"
                          : "expired"
                          }`}
                      >
                        {userSubscription.status.toUpperCase()}
                      </span>
                    </div>

                  </div>
                </div>
              ) : (
                <div className="cd-doc-empty">
                  <FaFileAlt />
                  <p>No subscription found.</p>
                </div>
              )}
            </Tab>


          </Tabs>
        </div>
      </Container>

      <Modal
        show={showPlanModal}
        onHide={() => setShowPlanModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Plan</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Plan</Form.Label>

            <Select
              placeholder="Select Plan"
              value={selectedPlan}
              onChange={setSelectedPlan}
              options={plans.map((plan) => ({
                value: plan._id,
                label: `${plan.name} (₹${plan.price})`,
              }))}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="cancel-btn"
            onClick={() => setShowPlanModal(false)}
          >
            Cancel
          </button>

          <button type="button"
            className="submit-btn"
            onClick={handleAssignPlan}
            disabled={assigning}
          >
            {assigning ? "Saving..." : "Save"}
          </button>
        </Modal.Footer>
      </Modal>

    </>
  );
}
